import axios from "axios";
import { ChartAPI, ChartLegendResponse, StockShortsIntraday } from "@/models/stock-shorts";
import { ListQueryParams } from "@/models/table";
import { takeWarm } from "@/lib/warm-requests";

// The short-interest tab's first requests can be started from the overview (warmShortInterestTab,
// src/lib/short-interest-preload.ts, #1350); these keys are how the tab's own calls find them.
export const shortsWarmKeys = {
    timeRanges: () => "shorts:time-ranges",
    topBoxes: (tradingItemId: number, timeRange?: string) => `shorts:top-boxes:${tradingItemId}:${timeRange ?? ""}`,
    dailySeries: (id: number, type: string, timeRange: string, usStock?: boolean, identifier?: string) => `shorts:daily:${identifier}:${id}:${type}:${timeRange}:${usStock ? 1 : 0}`,
    finraPositions: (id: number) => `shorts:finra-positions:${id}`,
    esmaSummary: (id: number) => `shorts:esma-summary:${id}`,
};

export const getStockShortInterest = (tradingItemId: number, timeRange?: string) => takeWarm(shortsWarmKeys.topBoxes(tradingItemId, timeRange), () => fetchStockShortInterest(tradingItemId, timeRange));

export const fetchStockShortInterest = async (tradingItemId: number, timeRange?: string) => {
    const res = await axios.get<{ [key: string]: any }>(`/interface/api/short/tradingitem/${tradingItemId}/top-boxes`, {
        params: {
            timerange: timeRange,
        },
    });

    if (!res.data) return {};
    return res.data;
};

export const getStockShortsIntraday = async (tradingitem_id: number) => {
    const res = await axios.get<StockShortsIntraday>(`/interface/api/short/screener/tradingitem/${tradingitem_id}/`);

    return res.data;
};

// This endpoint will be removed
/* export const getStockShortExchangeReported = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, universe_id, next_page_url } = params;

    const url = next_page_url || `/API/v2/id/${universe_id}/short/list/exchange_reported?&dgformat`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    try {
        const res = await axios.get<List<StockShortExchangeReported, StockShortExchangeReportedHeaders>>(url, {
            params: {
                ...defaultParams,
            },
        });
        return res.data;
    } catch (err) {
        throw err;
    }
}; */

export const getStockShortsData = async (endpoint: string) => {
    const res = await axios.get<{ [key: string]: [number, number][] | boolean; obfuscated: boolean; ts_shortened: boolean }>(endpoint);

    return res.data;
};

export const getStockShortsAPIs = async (stockId: number, advanced: boolean, params?: {}): Promise<ChartAPI[]> => {
    const res = await axios.get<{ apis: ChartAPI[] }>(`/interface/api/short/tradingitem/${stockId}/chart/legend${advanced ? "?advanced" : ""}`, {
        params: {
            ...params,
            chart_type: "official",
        },
    });

    return res.data.apis;
};

export const getStockShortsAPIs2 = async (tradingitem: number, advanced: boolean, params?: {}) => {
    const res = await axios.get<ChartLegendResponse>(`/interface/api/short/tradingitem/${tradingitem}/chart/legend`, {
        params: {
            ...params,
        },
    });

    return res.data;
};

export const getStockShortsTimeRanges = () => takeWarm(shortsWarmKeys.timeRanges(), fetchStockShortsTimeRanges);

export const fetchStockShortsTimeRanges = async () => {
    const res = await axios.get("/API/v2/shorts_time_ranges");

    return res.data;
};

export const getStockShortsDailyInterestNew = (tradingItemId: number, type: string, timeRange: string, usStock?: boolean, identifier?: string) =>
    takeWarm(shortsWarmKeys.dailySeries(tradingItemId, type, timeRange, usStock, identifier), () => fetchStockShortsDailyInterestNew(tradingItemId, type, timeRange, usStock, identifier));

export const fetchStockShortsDailyInterestNew = async (tradingItemId: number, type: string, timeRange: string, usStock?: boolean, identifier?: string) => {
    const res = await axios.get(`/interface/api/short/${identifier}/${tradingItemId}/short/${type}/${usStock ? "" : "allflagged"}`, {
        params: {
            timerange: timeRange,
        },
    });
    return res.data;
};

export const getStockShortInterestPositions = (id: number) => takeWarm(shortsWarmKeys.finraPositions(id), () => fetchStockShortInterestPositions(id));

export const fetchStockShortInterestPositions = async (id: number) => {
    const res = await axios.get(`/interface/api/short/tradingitem/${id}/finra/position/chart`);

    return res.data;
};

export const getStockShortEsmaSummary = (id: number) => takeWarm(shortsWarmKeys.esmaSummary(id), () => fetchStockShortEsmaSummary(id));

export const fetchStockShortEsmaSummary = async (id: number) => {
    const res = await axios.get(`/interface/api/short/tradingitem/${id}/esma/position/summary`);

    return res.data;
};

export type IntradayShortSeries = { data: [number, number][]; obfuscated: boolean };

export const getBorrowedChange = async (securityId: number, prevDay: boolean = false): Promise<IntradayShortSeries> => {
    const session = prevDay ? "prev" : "current";
    const res = await axios.get<{ data: [number, number][]; obfuscated?: boolean }>(`/interface/api/short/security/${securityId}/intraday/borrow_return_diff/net/${session}`);

    return { data: res.data.data || [], obfuscated: res.data.obfuscated ?? false };
};

export const getCTBAvg = async (securityId: number, prevDay: boolean = false): Promise<IntradayShortSeries> => {
    const session = prevDay ? "prev" : "current";
    const res = await axios.get<{ data: [number, number][]; obfuscated?: boolean }>(`/interface/api/short/security/${securityId}/intraday/ctb_avg/new/${session}`);

    return { data: res.data.data || [], obfuscated: res.data.obfuscated ?? false };
};

export const getAvailabilityChartData = async (tradingItemId: number) => {
    const res = await axios.get(`/interface/api/short/tradingitem/${tradingItemId}/short/ibkr_shares/`);

    return res.data.data;
};

export const getCTBChartData = async (tradingItemId: number) => {
    const res = await axios.get(`/interface/api/short/tradingitem/${tradingItemId}/short/ibkr_fee/`);
    return res.data.data;
};

// DataGrid Controllers

export const getStockShortsChanges = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, universe_id, data, next_page_url } = params;

    const url = next_page_url || `interface/api/short/tradingitem/${universe_id}/esma/changes/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get(url, {
        params: {
            ...defaultParams,
        },
    });

    return res.data;
};

export const getPositionList = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, universe_id, data, next_page_url } = params;

    const tradingitem_id = data?.["tradingitem_id"] || universe_id;
    const region = data?.["isOnCustomViews"] ? "finra" : data?.["region"]?.toLowerCase() || "esma";
    const url = next_page_url || `/interface/api/short/tradingitem/${tradingitem_id}/${region}/position/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get(url, {
        params: {
            ...defaultParams,
        },
    });

    return res.data;
};

export const getStockShortsLargest = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, universe_id, next_page_url } = params;

    const url = next_page_url || `/interface/api/short/tradingitem/${universe_id}/esma/position/summary`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get(url, {
        params: {
            ...defaultParams,
        },
    });

    return res.data;
};

export const getIBKR = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, universe_id, next_page_url } = params;

    const url = next_page_url || `/interface/api/short/tradingitem/${universe_id}/ibkr/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get(url, {
        params: {
            ...defaultParams,
        },
    });

    return res.data;
};

export async function getStockShortsFlagsData(url: string, from: string, to: string) {
    const res = await axios.get(url, {
        params: {
            from_date: from,
            to_date: to,
        },
    });
    return res.data.data;
}
